import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { Chart } from "primereact/chart";
import { Dialog } from "primereact/dialog";
import moment from "moment";
import CommonReportFilter from "./CommonReportFilter";

const SlaReport = () => {
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    // Bar Graph code start here
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const stackedBarTopLabels = {
        id: 'stackedBarTopLabels',
        afterDatasetsDraw(chart) {
            const { ctx, data, scales: { x, y } } = chart;
            const metData = data.datasets[0]?.data || [];
            const breachData = data.datasets[1]?.data || [];
            ctx.save();
            ctx.font = 'bold 11px sans-serif';
            ctx.fillStyle = '#333';
            ctx.textAlign = 'center';
            metData.forEach((met, i) => {
                const breach = breachData[i] || 0;
                const total = (met || 0) + breach;
                const xPos = x.getPixelForValue(i);
                const yPos = y.getPixelForValue(total) - 6;
                ctx.fillText(`M-${met} / B-${breach}`, xPos, yPos);
            });
            ctx.restore();
        },
    };
    const jobState = useSelector((state) => state.job);
    const protrackReports = useSelector((state) => state.reports);
    const {
        slaDownloadReportList,
        slaMetBreachDownloadReportList,
        slaReportList,
    } = protrackReports;
    const {
        appLanguage,
    } = jobState;
    useEffect(() => {
        const slaGraphData = slaReportList?.slamvsb ? slaReportList?.slamvsb : [];
        // Convert "MonthYear" format to "MMM YY"
        const formatMonthYear = (monthYear) => {
            const [month, year] = monthYear.split("-");
            const date = new Date(`${month} 1, ${year}`);
            return date.toLocaleString("en-US", { month: "short", year: "2-digit" });
        };

        // Transform the data
        const transformedData = {
            labels: slaGraphData?.map(item => formatMonthYear(item.MonthYear)),
            datasets: [
                {
                    type: "bar",
                    label: appLanguage["report.sla.label.sla-meet"],
                    backgroundColor: '#7F7F7F',
                    data: slaGraphData?.map(item => item.totalcommsSLAMet),
                },
                {
                    type: "bar",
                    label: appLanguage["report.sla.label.total-sla-fail"],
                    backgroundColor: '#C00000',
                    data: slaGraphData?.map(item => item.totalcommsSLABreach),
                },
            ],
        };

        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue("--text-color");
        const textColorSecondary = documentStyle.getPropertyValue(
            "--text-color-secondary"
        );
        const surfaceBorder = documentStyle.getPropertyValue("--surface-border");
        const data = transformedData;
        const options = {
            maintainAspectRatio: false,
            aspectRatio: 1,
            plugins: {
                tooltips: {
                    mode: "index",
                    intersect: false,
                },
                legend: {
                    display: false
                },

                // legend: {
                //     labels: {
                //         color: textColor,
                //     },
                // },
            },
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                        display: false,
                    },
                },
                y: {
                    stacked: true,
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                    },
                },
                    afterDataLimits(scale) {
                        scale.max = scale.max * 1.15;
                    },
            },
        };
        setChartData(data);
        setChartOptions(options);
    }, [slaReportList, slaDownloadReportList, appLanguage]);

    const downloadSlaMetBreachReport = () => {
        downloadCsv(slaMetBreachDownloadReportList, 'sla_met_breach_report.csv');
    }
    const downloadSlaMatricsReport = () => {
        downloadCsv(slaDownloadReportList, 'sla_report.csv');
        // let filterParams = {
        //     clientId: clientId,
        //     userId: userId,
        //     typeId: tabViewType,
        //     channelType: filterChannelType,
        //     filterType: filterDocType,
        //     fromDate: reportStartDate ? moment(reportStartDate).format("YYYY-MM-DD") : moment(new Date(new Date().setFullYear(new Date().getFullYear() - 1))).format("YYYY-MM-DD"),
        //     toDate: reportEndDate ? moment(reportEndDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
        //     reportType: filterReportType,
        // };
        // dispatch(fetchSlaDownloadReportData(filterParams));

        // if (slaDownloadReportList) {
        //     setTimeout(() => {
        //         downloadCsv(slaDownloadReportList, 'sla_report.csv');
        //     }, 2000)
        // }
    }


    // Convert JSON to CSV
    const jsonToCsv = (jsonData) => {
        const isArray = Array.isArray(jsonData);
        if (isArray) {
            const headers = Object.keys(jsonData[0]);
            const csvRows = [];
            csvRows.push(headers.join(','));
            jsonData.forEach(item => {
                const values = headers.map(header => {
                    const value = item[header];
                    return `"${value !== null && value !== undefined ? value.toString().replace(/"/g, '""') : ''}"`;
                });
                csvRows.push(values.join(','));
            });
            return csvRows.join('\n');
        } else {
            const headers = Object.keys(jsonData);
            const values = headers.map(header => {
                const value = jsonData[header];
                return `"${value !== null && value !== undefined ? value.toString().replace(/"/g, '""') : ''}"`;
            });
            return `${headers.join(',')}\n${values.join(',')}`;
        }
    };

    // Trigger CSV download
    const downloadCsv = (jsonData, fileName) => {
        const csvData = jsonToCsv(jsonData);
        console.log('CSV Data:', csvData);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    return (
        <div className="tab-content-wrap">
            <div className="two-col">
                <div className="left-col">
                    <div className="left-col-inner-wrap">
                        <div className="left-col-inner-left">
                            <CommonReportFilter />
                        </div>

                        <div className="left-col-inner-right">
                            <div className="box-white">
                                <h3>
                                    {appLanguage["report.sla.label.sla-metrics"]}
                                    <span className="button-wrapper">
                                        <button title="Download Source Data" className="download-icon" onClick={downloadSlaMatricsReport}></button>
                                    </span>
                                </h3>
                                <div className="sla-matrics-box">
                                    <div className="total-count-box">
                                        <h5>{appLanguage["report.sla.label.total-comms"]}</h5>
                                        <h4>{slaReportList?.totalcomms ? slaReportList?.totalcomms : 0}</h4>
                                    </div>
                                    <div className="total-count-box">
                                        <h5>{appLanguage["report.sla.label.sla-compliance"]}</h5>
                                        <h4><span>{slaReportList?.SLACompliance ? slaReportList?.SLACompliance : 0}</span>%</h4>
                                    </div>
                                </div>
                                <ul className="sla-matrics-list">
                                    <li>
                                        <label>{appLanguage["report.sla.label.total-comms-sla-breach"]}:</label>
                                        <span>{slaReportList?.totalcommsSLABreach ? slaReportList?.totalcommsSLABreach : 0}</span>
                                    </li>
                                    <li>
                                        <label>{appLanguage["report.sla.label.lst-breach"]}:</label>
                                        <span>{slaReportList?.LastBreach ? moment(slaReportList?.LastBreach).format("DD MMM YYYY") : ''}</span>
                                        {/* <span>{slaReportList?.SLA[0]?.LastBreach}</span> */}
                                    </li>
                                    <li>
                                        <label>{appLanguage["report.sla.label.total-executions"]}:</label>
                                        <span>{slaReportList?.totalExecutions ? slaReportList?.totalExecutions : 0}</span>
                                    </li>
                                    <li>
                                        <label>{appLanguage["report.sla.label.total-comms-received"]}:</label>
                                        <span>{slaReportList?.totalcommsreceived ? slaReportList?.totalcommsreceived : 0}</span>
                                    </li>
                                    <li>
                                        <label>{appLanguage["report.sla.label.total-comms-processed"]}:</label>
                                        <span>{slaReportList?.totalcommsprocessed ? slaReportList?.totalcommsprocessed : 0}</span>
                                    </li>
                                    <li>
                                        <label>{appLanguage["report.sla.label.total-comms-complete"]}:</label>
                                        <span>{slaReportList?.totalcommsComplete ? slaReportList?.totalcommsComplete : 0}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="right-col">
                    <div className="box-white">
                        <h3>
                            {appLanguage["report.sla.label.sla-meet-vs-sla-breach"]}
                            <span className="button-wrapper">
                                <button title="Download Source Data" className="download-icon" onClick={downloadSlaMetBreachReport}></button>
                                <button
                                    title="Expand Chart"
                                    className="zoom-icon"
                                    onClick={() => navigate("/sla-expended-report")}
                                ></button>
                                {/* <button
                                    className="zoom-icon"
                                    onClick={() => setExpendedGraphDialog(true)}
                                ></button> */}
                            </span>
                        </h3>

                        <div className="reporting-sla-graph-wrap">
                            <Chart
                                type="bar"
                                data={chartData}
                                options={chartOptions}
                                plugins={[stackedBarTopLabels]}
                                style={{ minHeight: '390px' }}
                            />

                            {/* <Dialog
                                header="Header"
                                visible={visible}
                                modal={false}
                                style={{ width: "50vw" }}
                                onHide={() => setVisible(false)}
                            ></Dialog> */}
                        </div>
                    </div>
                </div>
            </div>
            {/* {ExpendGraphData()} */}
        </div>
    )
}

export default SlaReport;